"use client";

import { useQuery, useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import { Doc } from "@convex/_generated/dataModel";
import { EmailMessage } from "./email-message";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Reply } from "lucide-react";
import { useEffect } from "react";
import { useNavigate } from "react-router";

interface EmailThreadProps {
  threadId: string;
  onBack: () => void;
}

export function EmailThread({ threadId, onBack }: EmailThreadProps) {
  const navigate = useNavigate();
  const thread = useQuery(api.email.threads.getThread, { threadId });
  const markAsRead = useMutation(api.email.threads.markThreadAsRead);

  // Mark as read when opened
  useEffect(() => {
    if (thread?.unreadIds.length) {
      markAsRead({ emailIds: thread.unreadIds });
    }
  }, [thread?.unreadIds, markAsRead]);

  if (!thread) {
    return <div className="p-4">Loading...</div>;
  }

  const handleReply = () => {
    const lastReceived = thread.messages
      .filter((m) => m.type === "received")
      .pop();
    if (lastReceived) {
      const email = lastReceived.email as Doc<"receivedEmails">;
      const params = new URLSearchParams({
        subject: `Re: ${email.subject}`,
        replyTo: email.fromEmail,
      });
      if (email.fromName) {
        params.set("replyToName", email.fromName);
      }
      navigate(`/email/compose?${params.toString()}`);
    }
  };

  return (
    <div className="flex flex-col h-full min-h-screen">
      <div className="sticky top-0 z-10 bg-background border-b px-4 pt-safe pb-3 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-lg font-semibold truncate flex-1 min-w-0">
          {thread.subject}
        </h1>
      </div>

      <div className="flex-1 overflow-auto overflow-x-hidden px-4 py-4 space-y-4">
        {thread.messages.map((msg) => (
          <EmailMessage
            key={msg.id}
            email={msg.email}
            type={msg.type}
          />
        ))}
      </div>

      {/* Sticky Reply Button - positioned above mobile nav */}
      <div className="fixed bottom-[calc(3.5rem+env(safe-area-inset-bottom))] left-0 right-0 z-10 bg-background/95 backdrop-blur border-t px-4 py-3">
        <div className="max-w-2xl mx-auto">
          <Button onClick={handleReply} className="w-full">
            <Reply className="h-4 w-4 mr-2" />
            Reply
          </Button>
        </div>
      </div>
      {/* Spacer for fixed button */}
      <div className="h-20" />
    </div>
  );
}
